import { useCallback, useLayoutEffect, useState, useRef, useMemo } from 'react';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import SelectionButton from 'modules/selection-button/index.js';
import PriceBoostBanner from 'modules/price-boost-banner/index.js';
import withEventCardSkeleton from 'modules/event-cards/skeleton/with-event-card-skeleton.js';
import { useEventItem, useMarketItems, useEventsLoadDone } from 'modules/common/hooks';
import MarketHelper from 'modules/market/helper/market-helper';

const {
    FlexPane,
    Button,
    Text,
    CompetitorIcon,
    SportIcon,
    CountryIcon,
    ParticipantItem,
    EventTimingStatus,
    SportWatermarkImage,
    SportWatermarkImageTypes,
    competitorIconHooks,
    scoreboardUtils: { sortParticipants },
} = MojitoPresentation.Components;
const { useCompetitorsIconsQuery } = competitorIconHooks;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { utils: EventUtils } = MojitoServices.SportsContent.Events;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;
const { EventFormatter } = MojitoPresentation.Utils;
const { PARTICIPANT_POSITION } = MojitoServices.SportsContent.Events.types;

const useHasMoreThanOneLine = lineHeight => {
    const ref = useRef();
    const [hasMoreThanOneLine, setHasMoreThanOneLine] = useState(false);
    useLayoutEffect(() => {
        const clientHeight = ref?.current?.clientHeight;
        setHasMoreThanOneLine(clientHeight > lineHeight);
    }, [ref?.current?.clientHeight, lineHeight]);
    return [ref, hasMoreThanOneLine];
};

const getSelectionNameHeight = (selectionNameConfig = {}) => {
    const oneLineHeightPx =
        selectionNameConfig.style?.lineHeight || selectionNameConfig.style?.fontSize;
    return (oneLineHeightPx && oneLineHeightPx.match(/\d+/)) || 0; // 0 will make market name truncate on one line.
};

const getMarketNameConfig = (hasPromotionText, hasMoreThanOneLine, config, style) => {
    if (hasPromotionText) {
        return config.marketName.withPromotionText;
    }
    if (!hasMoreThanOneLine) {
        return config.marketName.default;
    }
    return style.marketNameOneRowTruncation;
};

const useLoadDone = props => useEventsLoadDone([props.eventId]);

const bogusSelection = {
    id: 'BOGUS-selection',
    type: 'UNKNOWN',
};

const SelectionCard = withEventCardSkeleton(
    props => {
        const {
            eventId,
            marketId,
            selectionId,
            promotionText,
            mojitoTools,
            shouldRequestData,
            elementRef,
        } = props;
        const { config, appContext, l10n } = mojitoTools;
        const event = useEventItem(eventId, shouldRequestData);
        const market = useMarketItems([marketId], shouldRequestData)[0] || {};
        const { oddsFormat } = appContext.userSettings();
        const selection = EventUtils.getSelectionFromMarket(market, selectionId);
        const handicapLabel = selection?.handicapLabel ? ` ${selection?.handicapLabel}` : '';
        const basePrice =
            selection?.basePrice && EventUtils.getOdds(selection.basePrice, oddsFormat, l10n);
        const isRacing = EventUtils.isRaceEventType(event?.eventType);

        const onEventClick = useCallback(() => {
            const { sportId, id: eventId } = event;

            if (isRacing) {
                const { meetingId } = event.details;
                IntentActionTrigger.showMeetingsRaces(sportId, meetingId, eventId);
            } else {
                IntentActionTrigger.showSportEvent(sportId, eventId);
            }
            appContext.analyticsEmitter.emitAnalytics('eventCardClicked', event.canonicalName);
        }, [event, isRacing, appContext.analyticsEmitter]);

        if (!event) {
            return null;
        }

        const participants = useMemo(
            () => sortParticipants(event.participants, event.homeAway),
            [event.homeAway, event.participants]
        );

        const shouldShowLiveLabel = !basePrice && event.details.inPlay;

        const cardConfig = basePrice ? config.boostedEventCard : config.genericEventCard;
        const showBackground =
            config.showBackgroundSportImage &&
            !config.noBackgroundImageSports.includes(event.sportId);
        const hasPromotionText = Boolean(promotionText);

        return (
            <FlexPane
                class="ta-SelectionCard"
                config={cardConfig.container}
                elementRef={elementRef}
            >
                {showBackground && (
                    <BackgroundSportImage
                        {...props}
                        basePrice={basePrice}
                        sportId={event.sportId}
                    />
                )}
                <Button onClick={onEventClick} config={cardConfig.clickableArea}>
                    <CardHeader
                        {...props}
                        event={event}
                        participants={participants}
                        shouldShowLiveLabel={shouldShowLiveLabel}
                        isBoosted={Boolean(basePrice)}
                    />
                    <CardBody
                        {...props}
                        event={event}
                        selection={selection}
                        participants={participants}
                        handicapLabel={handicapLabel}
                        hasPromotionText={hasPromotionText}
                    />
                </Button>
                {basePrice ? (
                    <PriceBoostBanner
                        config={config.priceBoostBanner}
                        event={event}
                        market={market}
                        selection={selection}
                        basePrice={basePrice}
                    />
                ) : (
                    <FlexPane config={config.selectionContainer}>
                        <SelectionButton
                            config={config.selectionButton}
                            selection={selection || bogusSelection}
                            event={event}
                            marketStatus={market.status}
                            marketType={market.type}
                            marketId={market.id}
                        />
                    </FlexPane>
                )}
            </FlexPane>
        );
    },
    useLoadDone,
    config => config.genericEventCard.skeletonLoader
);

const CardHeader = props => {
    const { event, mojitoTools, isBoosted } = props;
    const { config, appContext, stringResolver } = mojitoTools;

    const altText =
        appContext.sportName(event.sportId) || stringResolver.resolveString('$GENERIC.DEFAULT');

    const isOutright = EventUtils.isOutrightEventType(event.eventType);
    const isRacing = EventUtils.isRaceEventType(event.eventType);
    const cardConfig = isBoosted ? config.boostedEventCard : config.genericEventCard;

    return (
        <FlexPane class={'ta-headerContainer'} config={config.headerContainer}>
            {config.showSportIcon && (
                <SportIcon sportId={event.sportId} config={config.sportIcon} alt={altText} />
            )}
            {config.showCountryIcon && (
                <CountryIcon
                    config={config.countryIcon}
                    countryCode={event.countryCode}
                    countryName={event.countryName}
                />
            )}
            {isOutright || isRacing ? (
                <Text class={'ta-eventName'} config={cardConfig.title}>
                    {`${EventFormatter.formatEventName(event.name)}`}
                </Text>
            ) : (
                <Participants {...props} />
            )}
            {!isOutright && <EventStatusInfo {...props} />}
        </FlexPane>
    );
};

const Participants = props => {
    const { event, mojitoTools, participants } = props;
    const { config, style, stringResolver } = mojitoTools;

    const { icons } = useCompetitorsIconsQuery({
        sportId: event.sportId,
        competitors: participants,
        config: config.competitorIconOptions,
    });

    if (!event.participants) {
        return null;
    }

    const separatorLabel = stringResolver.resolveString('$SELECTION_CARD.SEPARATOR_LABEL');

    if (EventUtils.isThreeParticipantsEvent(event)) {
        const participantNames = participants.map(p => p.name).join(` ${separatorLabel} `);
        return <Text config={config.threeParticipantText}>{participantNames}</Text>;
    }

    const [participantOne = {}, participantTwo = {}] = participants;

    return (
        <>
            <ParticipantItem
                sportId={event.sportId}
                participant={participantOne}
                config={style.participantItem}
                src={icons[0]}
            />
            <Text config={config.separatorLabel}>{separatorLabel}</Text>
            <ParticipantItem
                sportId={event.sportId}
                participant={participantTwo}
                config={config.participantItem}
                src={icons[1]}
            />
        </>
    );
};

const EventStatusInfo = props => {
    const { event, mojitoTools, isBoosted } = props;
    const { config, stringResolver } = mojitoTools;
    const shouldShowLiveLabel = !isBoosted && event.details.inPlay;

    return shouldShowLiveLabel ? (
        <Text config={config.headerLiveLabel}>
            {stringResolver.resolveString('$SELECTION_CARD.LIVE')}
        </Text>
    ) : (
        <EventTimingStatus
            config={config.headerTime}
            eventItem={event}
            hasStreaming={VideoStreamsUtils.isStreamAvailable(event)}
            hasClockDetails={EventUtils.hasInPlayClockDetails(event)}
            inplayTime={EventUtils.getEventInplayTime(event)}
        />
    );
};

const CardBody = props => {
    const {
        event,
        selectionName,
        handicapLabel,
        marketName,
        hasPromotionText,
        promotionText,
        mojitoTools,
        selection = {},
        participants,
    } = props;
    const { config, style } = mojitoTools;

    const [selectionNameRef, hasMoreThanOneLine] = useHasMoreThanOneLine(
        Number(getSelectionNameHeight(config.selectionName.default))
    );

    const { icons } = useCompetitorsIconsQuery({
        sportId: event.sportId,
        competitors: participants,
        config: config.competitorIconOptions,
    });

    if (!participants) {
        return null;
    }

    const { AWAY, HOME } = PARTICIPANT_POSITION;
    let position;
    if (MarketHelper.isHomeTeam(selection)) {
        position = HOME;
    } else if (MarketHelper.isAwayTeam(selection)) {
        position = AWAY;
    }

    const participantIndex = position ? participants.findIndex(p => p.position === position) : -1;
    const teamId = participants[participantIndex]?.id;
    const title = participants[participantIndex]?.name;
    const iconSrc = icons[participantIndex];

    const marketNameConfig = getMarketNameConfig(
        hasPromotionText,
        hasMoreThanOneLine,
        config,
        style
    );

    return (
        <FlexPane config={config.selectionBody}>
            <CompetitorIcon
                src={iconSrc}
                title={title}
                teamId={teamId}
                config={config.competitorIconBody}
            />
            <FlexPane config={config.selectionInfo} class="ta-selectionInfo">
                {hasPromotionText && (
                    <Text config={style.promotionTextOneRowTruncation}>{promotionText}</Text>
                )}
                <Text
                    config={
                        hasPromotionText
                            ? config.selectionName.withPromotionText
                            : config.selectionName.default
                    }
                    textRef={selectionNameRef}
                >
                    {`${selectionName} ${handicapLabel}`}
                </Text>
                <Text config={marketNameConfig}>{marketName}</Text>
            </FlexPane>
        </FlexPane>
    );
};

const BackgroundSportImage = props => {
    const { mojitoTools, sportId } = props;
    const { config } = mojitoTools;
    return (
        <SportWatermarkImage
            sportId={sportId}
            config={config.sportWatermark}
            position={SportWatermarkImageTypes.POSITIONS.TOP_RIGHT}
        />
    );
};

SelectionCard.getStyle = (config, applicationMode, merge) => {
    return {
        promotionTextOneRowTruncation: merge(config.promotionText, {
            ellipsisOnLineNumber: 1,
        }),
        marketNameOneRowTruncation: merge(config.marketName.default, { ellipsisOnLineNumber: 1 }),
        participantItem: merge(config.participantItem, {
            participantWrapper: { style: { flexDirection: 'row-reverse' } },
        }),
    };
};

export default SelectionCard;
